import { clamp } from "@/utilities/clamp";

/** A rectangle in screen pixels. */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A name that wants to be drawn next to a vehicle. */
export interface AssetLabelCandidate {
  id: string;
  text: string;
  /** Centre of the vehicle icon, in screen pixels. */
  anchorX: number;
  anchorY: number;
  /** Half the width of the icon, so a label is not placed on top of it. */
  iconHalfPx: number;
  /**
   * Room to reserve beside the text, for anything else drawn inside the box — the drive-type
   * icon, a charging bolt, a percentage. The caller measures it, because the caller is what
   * draws it.
   */
  extraWidth?: number;
}

/** Where the name ended up, and how big it is. */
export type AssetLabel = AssetLabelCandidate & Rect;

export interface AssetLabelLayoutOptions {
  /**
   * Areas a name must not cover although nothing is written on them: cluster bubbles, and the
   * icons of vehicles whose name has not arrived yet.
   */
  blockers?: Rect[];
  /**
   * Width of the drawn text in pixels, excluding padding. Without one the character count is
   * used, which is only a guess — the caller measures the real font where it can.
   */
  measureTextWidth?: (text: string) => number;
}

/**
 * Font and spacing of a name box.
 *
 * The layout reserves exactly the box that gets drawn, so both sides have to agree: a name
 * measured in one font and drawn in another is either clipped or sits off-centre in a box too
 * big for it.
 */
export const LABEL_FONT_SIZE_PX = 12;
export const LABEL_FONT_FAMILY = "Roboto, Helvetica, Arial, sans-serif";
export const LABEL_PADDING_X = 8;
export const LABEL_PADDING_Y = 5;
export const LABEL_LINE_HEIGHT = 14;

const MAX_WIDTH = 220;
/** Fallback width of a character, for sizing when the real font cannot be measured. */
const CHARACTER_WIDTH = 7.5;
/** Distance kept between an icon and its name. */
const GAP = 6;
/** Extra margin around an icon, so a name never quite touches one. */
const ICON_MARGIN = 4;
/** How far out the search goes when every direction next to the icon is taken. */
const MAX_RINGS = 8;
const RING_STEP_X = 14;
const RING_STEP_Y = 10;
/**
 * Side of a bucket in the occupancy grid. Roughly a label wide, so a label falls into a handful
 * of buckets and a placement is tested against a handful of neighbours.
 */
const GRID_CELL_PX = 64;
/** Folds a cell's column and row into one number, so the buckets key off a number, not a string. */
const GRID_COLUMN_STRIDE = 8192;

/** Tried in order, so a name goes above its vehicle unless something is already there. */
const DIRECTIONS: [number, number][] = [
  [0, -1], // above
  [1, -1], // above right
  [-1, -1], // above left
  [1, 0], // right
  [-1, 0], // left
  [1, 1], // below right
  [-1, 1], // below left
  [0, 1], // below
];

/**
 * Places the vehicle names so that they do not cover each other or the icons.
 *
 * With a depot full of vehicles the names land on top of one another and the map becomes a
 * smear — the one thing a reader wants from names is to tell two vehicles apart. Each name is
 * offered the eight positions around its own icon, then rings further out; the caller draws a
 * thin line back to the icon for any name that had to move (see {@link nearestPointOnRect}).
 *
 * Placement is first-come, so the caller decides who gets the good spots by the order it passes
 * them in — a selected vehicle first, say.
 *
 * @param candidates - The names to place, in priority order.
 * @param viewportWidth - Width of the map in pixels.
 * @param viewportHeight - Height of the map in pixels.
 * @param options - Further obstacles, and a way to measure the real text width.
 * @returns One entry per candidate, in the same order.
 */
export function buildAssetLabelLayout(
  candidates: AssetLabelCandidate[],
  viewportWidth: number,
  viewportHeight: number,
  options: AssetLabelLayoutOptions = {},
): AssetLabel[] {
  const { blockers = [], measureTextWidth } = options;
  const occupied = new OccupancyGrid();

  for (const blocker of blockers) {
    occupied.add(blocker);
  }

  // The icons are blocked out first, all of them, so that the first name placed cannot land on
  // an icon whose own name has not been dealt with yet.
  for (const candidate of candidates) {
    occupied.add(iconRect(candidate));
  }

  return candidates.map(candidate => {
    const textWidth = measureTextWidth
      ? measureTextWidth(candidate.text)
      : candidate.text.length * CHARACTER_WIDTH;
    const width = Math.min(
      MAX_WIDTH,
      Math.ceil(textWidth + (candidate.extraWidth ?? 0)) + LABEL_PADDING_X * 2,
    );
    const height = LABEL_PADDING_Y * 2 + LABEL_LINE_HEIGHT;

    const placed =
      findFreePlacement(candidate, width, height, occupied, viewportWidth, viewportHeight) ??
      above(candidate, width, height, viewportWidth, viewportHeight);

    occupied.add(placed);
    return { ...candidate, ...placed };
  });
}

/** The area an icon takes up, with a little air around it. */
export function iconRect(
  candidate: Pick<AssetLabelCandidate, "anchorX" | "anchorY" | "iconHalfPx">,
): Rect {
  const half = candidate.iconHalfPx + ICON_MARGIN;
  return {
    x: candidate.anchorX - half,
    y: candidate.anchorY - half,
    width: half * 2,
    height: half * 2,
  };
}

/**
 * The point on a name's box closest to its vehicle, for drawing the line between them.
 *
 * @param rect - The name's box.
 * @param x - The vehicle's position.
 * @param y - The vehicle's position.
 */
export function nearestPointOnRect(rect: Rect, x: number, y: number): [number, number] {
  return [clamp(x, rect.x, rect.x + rect.width), clamp(y, rect.y, rect.y + rect.height)];
}

/**
 * The rectangles already taken, bucketed by position.
 *
 * A plain array would do, but the layout re-runs on every animation frame and asks the same
 * question a few hundred times per run; scanning everything placed so far turns a depot into a
 * stutter exactly when the names are switched on.
 */
class OccupancyGrid {
  private readonly cells = new globalThis.Map<number, Rect[]>();

  // Both methods walk the cells themselves rather than share a callback: a placement is tried up
  // to 72 times per name, so a closure per call is thousands of throwaway objects per frame.

  add(rect: Rect) {
    const lastColumn = Math.floor((rect.x + rect.width) / GRID_CELL_PX);
    const lastRow = Math.floor((rect.y + rect.height) / GRID_CELL_PX);

    for (let column = Math.floor(rect.x / GRID_CELL_PX); column <= lastColumn; column++) {
      for (let row = Math.floor(rect.y / GRID_CELL_PX); row <= lastRow; row++) {
        const key = column * GRID_COLUMN_STRIDE + row;
        const bucket = this.cells.get(key);
        if (bucket) {
          bucket.push(rect);
        } else {
          this.cells.set(key, [rect]);
        }
      }
    }
  }

  hits(rect: Rect): boolean {
    const lastColumn = Math.floor((rect.x + rect.width) / GRID_CELL_PX);
    const lastRow = Math.floor((rect.y + rect.height) / GRID_CELL_PX);

    for (let column = Math.floor(rect.x / GRID_CELL_PX); column <= lastColumn; column++) {
      for (let row = Math.floor(rect.y / GRID_CELL_PX); row <= lastRow; row++) {
        const bucket = this.cells.get(column * GRID_COLUMN_STRIDE + row);
        if (!bucket) continue;
        for (const existing of bucket) {
          if (intersects(existing, rect)) {
            return true;
          }
        }
      }
    }

    return false;
  }
}

function findFreePlacement(
  candidate: AssetLabelCandidate,
  width: number,
  height: number,
  occupied: OccupancyGrid,
  viewportWidth: number,
  viewportHeight: number,
): Rect | null {
  // Ring 0 is right next to the icon; each further ring steps outwards in the same directions.
  for (let ring = 0; ring <= MAX_RINGS; ring++) {
    for (const [dx, dy] of DIRECTIONS) {
      const rect = placement(candidate, width, height, ring, dx, dy, viewportWidth, viewportHeight);
      if (!occupied.hits(rect)) {
        return rect;
      }
    }
  }

  return null;
}

/**
 * Where a name goes when the map is too crowded to find it a free spot: above its vehicle, which
 * is the first place it was offered.
 */
function above(
  candidate: AssetLabelCandidate,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): Rect {
  const [dx, dy] = DIRECTIONS[0];
  return placement(candidate, width, height, 0, dx, dy, viewportWidth, viewportHeight);
}

/** One of the positions a name is offered: `ring` steps out from the icon, in direction dx/dy. */
function placement(
  candidate: AssetLabelCandidate,
  width: number,
  height: number,
  ring: number,
  dx: number,
  dy: number,
  viewportWidth: number,
  viewportHeight: number,
): Rect {
  const reachX = candidate.iconHalfPx + GAP + (ring === 0 ? width / 2 : ring * RING_STEP_X);
  const reachY = candidate.iconHalfPx + GAP + (ring === 0 ? height / 2 : ring * RING_STEP_Y);

  return insideViewport(
    {
      x: candidate.anchorX + dx * reachX - width / 2,
      y: candidate.anchorY + dy * reachY - height / 2,
      width,
      height,
    },
    viewportWidth,
    viewportHeight,
  );
}

/** Nudges a name back inside the map. Returns the rectangle untouched when it already fits. */
function insideViewport(rect: Rect, viewportWidth: number, viewportHeight: number): Rect {
  const x = clamp(rect.x, 0, Math.max(0, viewportWidth - rect.width));
  const y = clamp(rect.y, 0, Math.max(0, viewportHeight - rect.height));
  return x === rect.x && y === rect.y ? rect : { ...rect, x, y };
}

function intersects(first: Rect, second: Rect) {
  return (
    first.x < second.x + second.width &&
    first.x + first.width > second.x &&
    first.y < second.y + second.height &&
    first.y + first.height > second.y
  );
}
